import type { FormikActions, FormikProps } from 'formik';
import { Formik, ErrorMessage } from 'formik';
import { Log } from 'ng2-logger';
import React from 'react';
import { connect } from 'react-redux';
import Translator from '../../services/translate-factory';
import * as Actions from '../../store/actions/general';
import * as actions from '../../store/constants/all';
import * as Constants from '../../store/constants/all';
import { TwoFactorValidationResult } from '../../store/constants/login-const';
import type * as Types from '../../store/types';
import TwoFactorModal from './TwoFactorModal';
import ForgotFormValidation from './validations/forgot-form-val';
import { LoginFormValidation } from './validations/login-form-val';

declare const appSettings: Types.IAppSettings;

const T = Translator.create();
const L = Log.create('LoginForm');

export enum LoginArea {
  LOGIN = 'login',
  FORGOT = 'forgot',
}

class LoginForm extends React.Component<Types.ILoginPageProps, Types.ILoginPageState> {
  state: Types.ILoginPageState = {
    activeArea: LoginArea.LOGIN,
    twoFactorModal: {
      isOpen: false,
      method: 'email',
      email: '',
    },
    loginForm: {
      username_loginpage: '',
      password_loginpage: '',
      account_id: '',
    },
    forgotForm: {
      email: '',
      account_id: '',
      token: '',
      password: '',
      password_confirm: '',
      usernameOrEmail: '',
    },
    term_id: -1,
    term_type: -1,
    solution_id: -1,
    termDefinationModalIsOpen: false,
    institutionName: '',
    lastLoginCredentials: null,
  };

  langChanged = () => {
    try {
      this.forceUpdate();
    } catch (e) {
      L.error(e as string);
    }
  };

  loggerCheck(hostname: string) {
    try {
      if (hostname.split('.')[0] !== 'dev') {
        Log.setProductionMode();
      }
    } catch (e) {
      console.log(e);
    }
  }

  onLogin = (model: Types.ILoginView, LoginActions: FormikActions<Types.ILoginView>) => {
    if (this.props.account) {
      model.account_id = '1'; // this.props.account.account_id;
    }
    const { hostname } = new URL(appSettings.API_URL);

    const modelAPI: Types.ILoginViewAPI = {
      account_id: model.account_id,
      grant_type: 'aplan',
      username: model.username_loginpage,
      password: model.password_loginpage,
      address: hostname,
      port: window.location.port,
    };

    this.props.dispatch(
      Actions.ApiRequest(
        actions.login.LOGIN_AUTH_TOKEN,
        modelAPI,
        'login-form-spin',
        (data: any, status: number) => {
          if (status === 412 && data.required2Fa) {
            this.setState((state) => ({
              ...state,
              twoFactorModal: {
                isOpen: true,
                method: data.method,
                email: data.email,
              },
              // Store credentials for resend functionality
              lastLoginCredentials: modelAPI,
            }));
          }
        },
      ),
    );
    LoginActions.setSubmitting(false);
  };

  onCloseTwoFactorModal = () => {
    this.setState((state) => ({
      ...state,
      twoFactorModal: { ...state.twoFactorModal, isOpen: false },
      // Clear stored credentials for security
      lastLoginCredentials: null,
    }));
  };

  onForgot = (model: Types.IForgotView, LoginActions: FormikActions<Types.IForgotView>) => {
    if (this.props.account) {
      model.account_id = this.props.account.account_id;
    }
    const newModel = {
      usernameOrEmail: model.email,
    };
    this.props.dispatch(
      Actions.ApiRequest(actions.login.LOGIN_FORGOT_PASSWORD, newModel, 'login-form-spin'),
    );
    LoginActions.setSubmitting(false);
  };

  onAreaChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e && e.currentTarget && e.currentTarget.dataset.area) {
      e.preventDefault();
      this.setState({ activeArea: e.currentTarget.dataset.area as LoginArea });
    }
  };

  componentDidMount() {
    T.removeListener(Constants.gen.CORE_CHANGE_LANGUAGE, this.langChanged);
    T.addListener(Constants.gen.CORE_CHANGE_LANGUAGE, this.langChanged);
  }

  componentWillUnmount() {
    T.removeListener(Constants.gen.CORE_CHANGE_LANGUAGE, this.langChanged);
  }

  verify2FA = async (code: string) => {
    return new Promise<void>((resolve, reject) => {
      this.props.dispatch(
        Actions.ApiRequest(
          actions.login.LOGIN_VERIFY_2FA_CODE,
          { code, email: this.state.twoFactorModal.email },
          'login-form-spin',
          (data: any, status: number) => {
            if (status === 200 && data === TwoFactorValidationResult.Valid) {
              // Success - Valid verification
              this.onCloseTwoFactorModal();
              resolve();
            } else {
              // Handle different validation results using enum
              let errorMessage = '';
              switch (data) {
                case TwoFactorValidationResult.UnexpectedError:
                  errorMessage = T.t('gen_error_unexpected');
                  break;
                case TwoFactorValidationResult.NotAllowed:
                  errorMessage = T.t('2fa_error_not_allowed');
                  break;
                case TwoFactorValidationResult.IncorrectCode:
                  errorMessage = T.t('2fa_error_invalid_code');
                  break;
                case TwoFactorValidationResult.CodeExpired:
                  errorMessage = T.t('2fa_code_expired');
                  break;
                default:
                  errorMessage = T.t('2fa_error_invalid_code');
              }
              reject(new Error(errorMessage));
            }
          },
        ),
      );
    });
  };

  resend2FA = async () => {
    return new Promise<void>((resolve, reject) => {
      if (!this.state.lastLoginCredentials) {
        reject(new Error(T.t('2fa_error_resend_failed')));
        return;
      }

      this.props.dispatch(
        Actions.ApiRequest(
          actions.login.LOGIN_AUTH_TOKEN,
          this.state.lastLoginCredentials,
          'login-form-spin',
          (data: any, status: number) => {
            if (status === 412 && data.required2Fa) {
              // Successfully triggered new 2FA code
              resolve();
            } else {
              reject(new Error(T.t('2fa_error_resend_failed')));
            }
          },
        ),
      );
    });
  };

  render() {
    if (this.state.activeArea === LoginArea.LOGIN) {
      return (
        <React.Fragment>
          <Formik
            initialValues={this.state.loginForm}
            onSubmit={this.onLogin}
            validationSchema={LoginFormValidation(T)}
          >
            {(props: FormikProps<Types.ILoginView>) => {
              const { values, handleChange, errors, handleBlur, handleSubmit } = props;
              return (
                <form onSubmit={props.handleSubmit}>
                  <div className="white-container mt-6">
                    <h3
                      className="primary-glyph personnel-login-tag"
                      style={{ paddingBottom: '4px' }}
                    >
                      {T.t('gen_personnel_login')}
                    </h3>
                    <div className="form-group">
                      <div className="form-input">
                        <input
                          id="username_loginpage"
                          type="text"
                          value={props.values.username_loginpage}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                        />
                        <span className="highlight"></span>
                        <span className="bar"></span>
                        <label>
                          {T.t('gen_email')} /
{T.t('gen_username')}
                        </label>
                      </div>
                      {/* todo: please check it */}
                      {errors &&
                        typeof errors.username_loginpage === 'string' &&
                        props.submitCount > 0 && (
                          <div className="error">{errors.username_loginpage}</div>
                        )}
                    </div>
                    <div className="form-group">
                      <div className="form-input">
                        <input
                          id="password_loginpage"
                          type="password"
                          value={props.values.password_loginpage}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                        />
                        <span className="highlight"></span>
                        <span className="bar"></span>
                        <label>{T.t('gen_password')}</label>
                      </div>
                      {errors &&
                        typeof errors.password_loginpage === 'string' &&
                        props.submitCount > 0 && (
                          <div className="error">{errors.password_loginpage}</div>
                        )}
                      <ErrorMessage component="div" className="error" name="password" />
                    </div>

                    <div className="row align-items-center">
                      <div className="col">
                        <button
                          type="button"
                          className="ghost"
                          data-area="forgot"
                          onClick={this.onAreaChange}
                        >
                          {T.t('gen_password_forgot')}
                        </button>
                      </div>
                      <div className="col text-right">
                        <button className="primary" type="submit" disabled={props.isSubmitting}>
                          {T.t('gen_login')} <i className="material-icons">arrow_forward</i>
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
          <TwoFactorModal
            isOpen={this.state.twoFactorModal.isOpen}
            onClose={this.onCloseTwoFactorModal}
            email={this.state.twoFactorModal.email}
            method={this.state.twoFactorModal.method}
            onSubmit={this.verify2FA}
            onResendCode={this.resend2FA}
          />
        </React.Fragment>
      );
    }
    if (this.state.activeArea === LoginArea.FORGOT) {
      return (
        <React.Fragment>
          <h3 className="mt-3">{T.t('gen_title_not_alone')}</h3>
          <div className="white-container mt-6">
            <p>{T.t('gen_password_forgot_body')}</p>
            <Formik
              initialValues={this.state.forgotForm}
              onSubmit={this.onForgot}
              validationSchema={ForgotFormValidation(T)}
            >
              {(props: FormikProps<Types.IForgotView>) => {
                return (
                  <form onSubmit={props.handleSubmit}>
                    <div className="form-group">
                      <div className="form-input">
                        <input
                          name="email"
                          type="text"
                          value={props.values.email}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                        />
                        <span className="highlight"></span>
                        <span className="bar"></span>
                        <label>{T.t('gen_email')}</label>
                      </div>
                      <ErrorMessage component="div" className="error" name="email" />
                    </div>

                    <div className="row align-items-center">
                      <div className="col">
                        <button
                          type="button"
                          className="ghost"
                          data-area="login"
                          onClick={this.onAreaChange}
                        >
                          <i className="material-icons mr-3">keyboard_backspace</i>
                          {T.t('gen_previous')}
                        </button>
                      </div>
                      <div className="col text-right">
                        <button className="primary" type="submit" disabled={props.isSubmitting}>
                          {T.t('gen_send')} <i className="material-icons">arrow_forward</i>
                        </button>
                      </div>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </div>
        </React.Fragment>
      );
    }
  }
}

const mapStateToProps = (
  store: Types.IPersistedState,
  ownProps: Types.ILoginPageProps,
): Types.ILoginPageProps => {
  if (!store?.state) {
    return ownProps;
  }

  const newProps: any = Object.assign({}, ownProps, {
    account: store.state.account,
    definitions: store.state.definitions,
  });

  return newProps;
};

const dispatchProps = (dispatch: any) => ({ dispatch });

const container = connect(mapStateToProps, dispatchProps)(LoginForm);

export default container;
